import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Container, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

import Breadcrumb from "../../common/breadcrumb";
import MultiSelectField from "../../common/MultiSelectField";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { RCard, Badge, Tile, Loader, TablePagination, usePagination } from "../../reporting/ReportingShared";
import {
  fetchAdminSnoozedProducts,
  snoozeProductsAdmin,
  unsnoozeProductsAdmin,
  fetchBranches,
  getProductListing,
} from "../../../utils/apiCalls";
import { errorToast } from "../../../shared/Toast";
import { getProductImageUrl } from "../../../utils/imageUtils";
import { isMerchantScoped, isReadOnly } from "../../../constants/roles";

// Branch-level product snooze: take an item off the online menu for a while, across as many branches
// as needed.
//
// WHY THIS PAGE EXISTS AND NOT JUST THE POS
// The POS has the same feature (PickupSnoozeOptionsModal) but it is bound to the one branch that
// device sits in. A merchant with twenty branches cannot say "this is out everywhere" without twenty
// people touching twenty tills. That is the whole reason for the multi-branch selection below.
//
// WHAT A SNOOZE IS NOT
// It is not "we do not sell this here" — that is BranchUnavailableProducts, which the Odoo menu sync
// owns and this page cannot write. Hence the 90-day cap on the server: anything longer is really the
// other thing, and the report says so.

/** Presets, in the order an operator reaches for them. `endOfDay` is resolved server-side. */
const DURATIONS = [
  { key: "1h", label: "1 hour", snoozeHours: 1 },
  { key: "4h", label: "4 hours", snoozeHours: 4 },
  { key: "eod", label: "End of day", untilEndOfDay: true },
  { key: "24h", label: "24 hours", snoozeHours: 24 },
  { key: "7d", label: "7 days", snoozeHours: 24 * 7 },
  { key: "custom", label: "Pick a time…", custom: true },
];

const PRODUCT_PAGE_SIZE = 25;

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const timeRemaining = (value) => {
  if (!value) return "—";
  const ms = new Date(value).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

const rowKey = (row) => `${row.branchId ?? row.Branch?.id}:${row.productId ?? row.Product?.id}`;
const rowBranchId = (row) => row.branchId ?? row.Branch?.id;
const rowProductId = (row) => row.productId ?? row.Product?.id;

const msLeft = (value) => new Date(value).getTime() - Date.now();

// "Back within the hour" — what the shift needs to know is coming back on its own.
const SOON_MS = 60 * 60 * 1000;
// A week or more is rarely "ran out until tomorrow". If the item is not sold at that branch any
// more, removing it from the branch in the merchant system is the honest fix (see the header).
const LONG_MS = 7 * 24 * 60 * 60 * 1000;

const VIEWS = [
  { key: "product", label: "By product" },
  { key: "branch", label: "By branch" },
  { key: "rows", label: "All rows" },
];

/** Earliest and latest return of a group, for "Back on the menu" on a grouped row. */
const returnRange = (rows) => {
  const times = rows.map((row) => new Date(row.snooze_until).getTime()).sort((a, b) => a - b);
  return { first: times[0], last: times[times.length - 1] };
};

const SnoozedProducts = () => {
  const userType = localStorage.getItem("userType");
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  // Merchant-scoped roles have no header switcher, so their merchant comes from storage.
  const selectedMerchant = isMerchantScoped(userType) ? merchantIdFromStorage : reduxSelectedMerchant;
  // Belt and braces: `operations` cannot reach this path today (FullLayout bounces anything not in
  // OPERATIONS_ALLOWED_PATHS), but the guard is the only mechanism that exists and costs nothing.
  const readOnly = isReadOnly(userType);

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [view, setView] = useState("product");
  const [quick, setQuick] = useState("all"); // all | soon | long — set by clicking a summary tile
  const [expanded, setExpanded] = useState(new Set()); // group keys opened in the grouped views

  // ── snooze modal
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [modalBranches, setModalBranches] = useState([]); // [{value,label}]
  const [duration, setDuration] = useState("1h");
  const [customUntil, setCustomUntil] = useState("");
  const [picked, setPicked] = useState(new Set()); // product ids
  const [pickedLabels, setPickedLabels] = useState(new Map()); // id -> title, for the summary
  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── report modal — every write ends here, because a partial success is the normal outcome
  const [report, setReport] = useState(null);

  const noMerchant = !selectedMerchant || selectedMerchant === "all";

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(id);
  }, [searchText]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedProductSearch(productSearch.trim()), 300);
    return () => clearTimeout(id);
  }, [productSearch]);

  const load = useCallback(async () => {
    if (noMerchant) {
      setRows([]);
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchAdminSnoozedProducts({
        merchantId: selectedMerchant,
        branchId: branchFilter,
        search: debouncedSearch,
      });
      setRows(Array.isArray(data) ? data : []);
      setSelectedKeys(new Set());
    } finally {
      setIsLoading(false);
    }
  }, [selectedMerchant, branchFilter, debouncedSearch, noMerchant]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const loadBranches = async () => {
      const response = await fetchBranches({ merchantID: selectedMerchant }, false);
      setBranches(response?.data || []);
    };
    if (!noMerchant) loadBranches();
    else setBranches([]);
  }, [selectedMerchant, noMerchant]);

  // The product picker. `branchId` is passed only when exactly one branch is chosen: with several,
  // there is no single branch whose hidden products should be filtered out, and the server reports
  // per-branch skips anyway.
  const soleBranchId = modalBranches.length === 1 ? modalBranches[0].value : undefined;

  const loadProducts = useCallback(async () => {
    if (noMerchant || !snoozeOpen) return;
    setProductsLoading(true);
    try {
      const result = await getProductListing(
        selectedMerchant,
        { search: debouncedProductSearch || undefined, branchId: soleBranchId },
        productPage,
        PRODUCT_PAGE_SIZE
      );
      setProducts(result?.data || []);
      setProductTotal(result?.totalCount || 0);
    } finally {
      setProductsLoading(false);
    }
  }, [selectedMerchant, debouncedProductSearch, productPage, soleBranchId, snoozeOpen, noMerchant]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    setProductPage(1);
  }, [debouncedProductSearch, soleBranchId]);

  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, label: b.name })),
    [branches]
  );

  // Paused = the whole branch is off for online orders (the POS switch), which no product row shows.
  const activeBranches = useMemo(() => branches.filter((b) => b.active !== false), [branches]);
  const pausedBranches = useMemo(() => activeBranches.filter((b) => b.snooze_online_orders), [activeBranches]);
  const pausedIds = useMemo(() => new Set(pausedBranches.map((b) => b.id)), [pausedBranches]);

  const summary = useMemo(() => ({
    products: new Set(rows.map(rowProductId)).size,
    branches: new Set(rows.map(rowBranchId)).size,
    soon: rows.filter((row) => msLeft(row.snooze_until) <= SOON_MS).length,
    long: rows.filter((row) => msLeft(row.snooze_until) >= LONG_MS).length,
  }), [rows]);

  const visibleRows = useMemo(() => {
    if (quick === "soon") return rows.filter((row) => msLeft(row.snooze_until) <= SOON_MS);
    if (quick === "long") return rows.filter((row) => msLeft(row.snooze_until) >= LONG_MS);
    return rows;
  }, [rows, quick]);

  // One line per product: "is this out everywhere, or at two branches?" is the question this answers.
  const byProduct = useMemo(() => {
    const map = new Map();
    visibleRows.forEach((row) => {
      const id = rowProductId(row);
      if (!map.has(id)) map.set(id, { key: `p:${id}`, product: row.Product, plu: row.merchant_product_id, rows: [] });
      map.get(id).rows.push(row);
    });
    return [...map.values()]
      .map((group) => ({ ...group, ...returnRange(group.rows) }))
      .sort((a, b) => b.rows.length - a.rows.length || (a.product?.title || "").localeCompare(b.product?.title || ""));
  }, [visibleRows]);

  const byBranch = useMemo(() => {
    const map = new Map();
    visibleRows.forEach((row) => {
      const id = rowBranchId(row);
      if (!map.has(id)) map.set(id, { key: `b:${id}`, branchId: id, name: row.Branch?.name || `Branch #${id}`, rows: [] });
      map.get(id).rows.push(row);
    });
    return [...map.values()]
      .map((group) => ({ ...group, ...returnRange(group.rows) }))
      .sort((a, b) => b.rows.length - a.rows.length || a.name.localeCompare(b.name));
  }, [visibleRows]);

  const listForView = view === "product" ? byProduct : view === "branch" ? byBranch : visibleRows;
  const { paged, page, limit, setPage, setLimit, total } = usePagination(listForView, 25);

  // usePagination resets only when the length changes; two views can have the same length.
  useEffect(() => {
    setPage(1);
    setExpanded(new Set());
  }, [view, quick, setPage]);

  const toggleExpanded = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openSnooze = () => {
    setModalBranches(branchFilter !== "all" ? branchOptions.filter((o) => String(o.value) === branchFilter) : []);
    setDuration("1h");
    setCustomUntil("");
    setPicked(new Set());
    setPickedLabels(new Map());
    setProductSearch("");
    setDebouncedProductSearch("");
    setProductPage(1);
    setSnoozeOpen(true);
  };

  const togglePicked = (product) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.add(product.id);
      return next;
    });
    setPickedLabels((prev) => {
      const next = new Map(prev);
      next.set(product.id, product.title);
      return next;
    });
  };

  const allOnPagePicked = products.length > 0 && products.every((p) => picked.has(p.id));

  const toggleAllOnPage = () => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (allOnPagePicked) products.forEach((p) => next.delete(p.id));
      else products.forEach((p) => next.add(p.id));
      return next;
    });
    setPickedLabels((prev) => {
      const next = new Map(prev);
      products.forEach((p) => next.set(p.id, p.title));
      return next;
    });
  };

  const handleSnooze = async () => {
    if (modalBranches.length === 0) {
      errorToast("Select at least one branch");
      return;
    }
    if (picked.size === 0) {
      errorToast("Select at least one product");
      return;
    }

    const chosen = DURATIONS.find((d) => d.key === duration);
    if (chosen?.custom && !customUntil) {
      errorToast("Pick a date and time");
      return;
    }

    setSaving(true);
    const result = await snoozeProductsAdmin({
      merchantId: selectedMerchant,
      branchIds: modalBranches.map((b) => b.value),
      productIds: [...picked],
      snoozeHours: chosen?.snoozeHours,
      untilEndOfDay: chosen?.untilEndOfDay,
      // `datetime-local` yields wall-clock text with no zone. `new Date(...)` reads it in the
      // browser's zone and toISOString sends a real instant, so the server is never guessing.
      snoozeUntil: chosen?.custom ? new Date(customUntil).toISOString() : undefined,
    });
    setSaving(false);

    if (result.report) setReport({ kind: "snooze", ...result.report });
    if (result.ok) {
      setSnoozeOpen(false);
      load();
    }
  };

  const unsnooze = async (targets) => {
    if (targets.length === 0) return;
    const label = targets.length === 1 ? `“${targets[0].productTitle}” at ${targets[0].branchName}` : `${targets.length} snoozes`;
    if (!window.confirm(`Un-snooze ${label}? The item goes back on the menu straight away.`)) return;

    // Grouped by branch so one call covers a bulk selection, which is also what makes the branch
    // menu stamp fire once per branch rather than once per row.
    const byBranch = new Map();
    targets.forEach((t) => {
      if (!byBranch.has(t.branchId)) byBranch.set(t.branchId, new Set());
      byBranch.get(t.branchId).add(t.productId);
    });

    const merged = { lifted: [], skipped: [] };
    let anyOk = false;

    for (const [branchId, productIds] of byBranch) {
      // eslint-disable-next-line no-await-in-loop
      const result = await unsnoozeProductsAdmin({
        merchantId: selectedMerchant,
        branchIds: [branchId],
        productIds: [...productIds],
      });
      if (result.ok) anyOk = true;
      if (result.report) {
        merged.lifted.push(...(result.report.lifted || []));
        merged.skipped.push(...(result.report.skipped || []));
      }
    }

    if (merged.skipped.length) setReport({ kind: "unsnooze", ...merged });
    if (anyOk) load();
  };

  const toRow = (row) => ({
    branchId: row.branchId ?? row.Branch?.id,
    productId: row.productId ?? row.Product?.id,
    branchName: row.Branch?.name || "—",
    productTitle: row.Product?.title || "—",
  });

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedKeys.has(rowKey(r))).map(toRow),
    [rows, selectedKeys]
  );

  const allPagedSelected = paged.length > 0 && paged.every((r) => selectedKeys.has(rowKey(r)));

  const toggleRow = (row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const key = rowKey(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllRows = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allPagedSelected) paged.forEach((r) => next.delete(rowKey(r)));
      else paged.forEach((r) => next.add(rowKey(r)));
      return next;
    });
  };

  const ghostButton = {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.textMed,
    fontWeight: 600,
    fontSize: 12,
    padding: "8px 16px",
  };
  const primaryButton = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    padding: "8px 16px",
  };
  const fieldStyle = { borderRadius: 8, border: `1px solid ${C.border}` };

  const sourceBadge = (row) => {
    if (row.snoozeSource === "admin") return <Badge color={C.coral} bg={C.coralLight}>Admin</Badge>;
    if (row.snoozeSource === "pos") return <Badge color={C.blue} bg={C.blueLight}>POS</Badge>;
    // Every row written before provenance existed, which is most of them on an existing database.
    return <span style={{ color: C.textLight }}>—</span>;
  };

  const snoozedByName = (row) => {
    const user = row.snoozedBy;
    if (!user) return null;
    return [user.firstName, user.lastName].filter(Boolean).join(" ") || null;
  };

  const byCell = (row) => {
    const by = snoozedByName(row);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {sourceBadge(row)}
        {by && <span style={{ fontSize: 12, color: C.textMed }}>{by}</span>}
      </div>
    );
  };

  const leftBadge = (value) => {
    const long = msLeft(value) >= LONG_MS;
    return (
      <span
        title={long ? "Snoozed for a week or more. If it is not sold at this branch any more, remove it from the branch in the merchant system instead." : undefined}
      >
        <Badge color={long ? C.purple : C.orange} bg={long ? C.purpleLight : C.orangeLight}>
          {timeRemaining(value)}
        </Badge>
      </span>
    );
  };

  const rangeText = (group) =>
    group.first === group.last ? formatDate(group.first) : `${formatDate(group.first)} – ${formatDate(group.last)}`;

  const productCell = (product, plu) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {(product?.image1 || product?.merchant_image) && (
        <img
          alt=""
          src={getProductImageUrl(product)}
          onError={(e) => { e.target.style.display = "none"; }}
          style={{ width: 34, height: 34, borderRadius: 6, objectFit: "cover" }}
        />
      )}
      <div>
        <div style={{ fontWeight: 600, color: C.text }}>{product?.title || "—"}</div>
        <code style={{ fontSize: 11, color: C.textLight }}>{plu}</code>
      </div>
    </div>
  );

  const branchLabel = (branchId, name) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontWeight: 600, color: C.text }}>{name}</span>
      {pausedIds.has(branchId) && <Badge color={C.coral} bg={C.coralLight}>Paused</Badge>}
    </div>
  );

  const branchNames = (groupRows) => {
    const names = groupRows.map((row) => row.Branch?.name || `#${rowBranchId(row)}`).sort();
    return names.length > 3 ? `${names.slice(0, 3).join(", ")} +${names.length - 3} more` : names.join(", ");
  };

  // By product / by branch: one line per group, click to see its rows. Un-snoozing a group goes
  // through the same per-branch calls as a bulk selection.
  const renderGrouped = () => {
    const isProduct = view === "product";
    const activeIds = new Set(activeBranches.map((b) => b.id));
    const smallButton = { ...ghostButton, color: C.coral, fontSize: 11, padding: "5px 12px" };

    return (
      <table style={tbl.table}>
        <thead>
          <tr>
            <th style={{ ...tbl.th, width: 36 }} />
            {[
              isProduct ? "Product" : "Branch",
              isProduct ? "Snoozed at" : "Products snoozed",
              "Back on the menu",
              "Next back in",
              ...(readOnly ? [] : [""]),
            ].map((label, i) => (
              <th key={`${label}-${i}`} style={{ ...tbl.th, ...(label === "" && { textAlign: "right" }) }}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paged.map((group) => {
            const open = expanded.has(group.key);
            const count = group.rows.length;
            const activeCount = group.rows.filter((row) => activeIds.has(rowBranchId(row))).length;
            const everywhere = isProduct && activeBranches.length > 1 && activeCount === activeBranches.length;

            return (
              <Fragment key={group.key}>
                <tr className="rpt-row" style={{ cursor: "pointer" }} onClick={() => toggleExpanded(group.key)}>
                  <td style={{ ...tbl.td, color: C.textLight }}>
                    <span style={{ display: "inline-block", transition: "transform .15s", transform: open ? "rotate(90deg)" : "none" }}>
                      ▶
                    </span>
                  </td>
                  <td style={tbl.td}>
                    {isProduct ? productCell(group.product, group.plu) : branchLabel(group.branchId, group.name)}
                  </td>
                  <td style={tbl.td}>
                    {isProduct ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, color: C.text }}>
                          {count} branch{count === 1 ? "" : "es"}
                        </span>
                        {everywhere ? (
                          <Badge color={C.orange} bg={C.orangeLight}>All active branches</Badge>
                        ) : (
                          <span style={{ fontSize: 12, color: C.textMed }}>{branchNames(group.rows)}</span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontWeight: 700, color: C.text }}>
                        {count} product{count === 1 ? "" : "s"}
                      </span>
                    )}
                  </td>
                  <td style={{ ...tbl.td, fontVariantNumeric: "tabular-nums" }}>{rangeText(group)}</td>
                  <td style={tbl.td}>{leftBadge(group.first)}</td>
                  {!readOnly && (
                    <td style={{ ...tbl.td, textAlign: "right" }}>
                      <Button
                        size="sm"
                        style={smallButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          unsnooze(group.rows.map(toRow));
                        }}
                      >
                        {count > 1 ? `Un-snooze all ${count}` : "Un-snooze"}
                      </Button>
                    </td>
                  )}
                </tr>

                {open &&
                  group.rows.map((row) => (
                    <tr key={rowKey(row)} style={{ background: C.bg }}>
                      <td style={tbl.td} />
                      <td style={{ ...tbl.td, paddingLeft: 24 }}>
                        {isProduct
                          ? branchLabel(rowBranchId(row), row.Branch?.name || "—")
                          : productCell(row.Product, row.merchant_product_id)}
                      </td>
                      <td style={tbl.td}>{byCell(row)}</td>
                      <td style={{ ...tbl.td, fontVariantNumeric: "tabular-nums" }}>{formatDate(row.snooze_until)}</td>
                      <td style={tbl.td}>{leftBadge(row.snooze_until)}</td>
                      {!readOnly && (
                        <td style={{ ...tbl.td, textAlign: "right" }}>
                          <Button size="sm" style={smallButton} onClick={() => unsnooze([toRow(row)])}>
                            Un-snooze
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Snoozed Products" parent="Products" />
      <Container fluid className="rpt-wrap">
        {!noMerchant && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: 12,
              marginBottom: 20,
            }}
          >
            {[
              {
                key: "products",
                label: "Snoozed products",
                value: summary.products,
                sub: `${rows.length} product × branch snooze${rows.length === 1 ? "" : "s"}`,
                icon: "🌙",
                gradient: `linear-gradient(135deg, ${C.orange}, #FFD200)`,
              },
              {
                key: "branches",
                label: "Branches affected",
                value: summary.branches,
                sub: `of ${activeBranches.length} active branch${activeBranches.length === 1 ? "" : "es"}`,
                icon: "🏪",
                gradient: `linear-gradient(135deg, ${C.blue}, #74b9ff)`,
              },
              {
                key: "paused",
                label: "Paused branches",
                value: pausedBranches.length,
                sub: "not taking any online orders",
                icon: "⏸",
                gradient: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
              },
              {
                key: "soon",
                label: "Back within 1 hour",
                value: summary.soon,
                sub: quick === "soon" ? "Showing only these — click to clear" : "Click to show only these",
                icon: "⏱",
                gradient: `linear-gradient(135deg, ${C.green}, #55efc4)`,
                filter: "soon",
              },
              {
                key: "long",
                label: "Snoozed 7+ days",
                value: summary.long,
                sub: quick === "long" ? "Showing only these — click to clear" : "Not sold here? Remove it from the branch instead",
                icon: "📌",
                gradient: `linear-gradient(135deg, ${C.purple}, #a29bfe)`,
                filter: "long",
              },
            ].map((tile, i) => (
              <div
                key={tile.key}
                role={tile.filter ? "button" : undefined}
                tabIndex={tile.filter ? 0 : undefined}
                onClick={tile.filter ? () => setQuick(quick === tile.filter ? "all" : tile.filter) : undefined}
                onKeyDown={tile.filter ? (e) => e.key === "Enter" && setQuick(quick === tile.filter ? "all" : tile.filter) : undefined}
                style={{
                  cursor: tile.filter ? "pointer" : "default",
                  borderRadius: 14,
                  outline: quick !== "all" && quick === tile.filter ? `2px solid ${C.coral}` : "none",
                }}
              >
                <Tile label={tile.label} value={tile.value} sub={tile.sub} icon={tile.icon} gradient={tile.gradient} delay={i * 60} />
              </div>
            ))}
          </div>
        )}

        {pausedBranches.length > 0 && (
          <RCard style={{ padding: "14px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, color: C.text, fontSize: 13 }}>Paused for online orders:</span>
              {pausedBranches.map((b) => (
                <Badge key={b.id} color={C.coral} bg={C.coralLight}>{b.name}</Badge>
              ))}
              <span style={{ fontSize: 12, color: C.textLight }}>
                The whole branch is off (POS switch or Edit Branch), whatever its products say.
              </span>
            </div>
          </RCard>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          {noMerchant && (
            <span style={{ color: C.orange, fontWeight: 600, fontSize: 13 }}>
              Please select a merchant from the dropdown
            </span>
          )}

          {!noMerchant && (
            <>
              <Input
                type="text"
                placeholder="Search products…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ ...fieldStyle, maxWidth: 260 }}
              />
              <Input
                type="select"
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                style={{ ...fieldStyle, maxWidth: 220 }}
              >
                <option value="all">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={String(b.id)}>{b.name}</option>
                ))}
              </Input>
              <div style={{ display: "inline-flex", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                {VIEWS.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setView(v.key)}
                    style={{
                      border: "none",
                      padding: "7px 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      background: view === v.key ? C.coral : C.white,
                      color: view === v.key ? "#fff" : C.textMed,
                    }}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </>
          )}

          <div style={{ flex: 1 }} />

          {!readOnly && view === "rows" && selectedRows.length > 0 && (
            <Button size="sm" style={ghostButton} onClick={() => unsnooze(selectedRows)}>
              Un-snooze selected ({selectedRows.length})
            </Button>
          )}
          <Button size="sm" style={ghostButton} onClick={load}>↻ Refresh</Button>
          {!readOnly && (
            <Button size="sm" style={primaryButton} disabled={noMerchant} onClick={openSnooze}>
              + Snooze products
            </Button>
          )}
        </div>

        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? (
            <Loader />
          ) : visibleRows.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>
              {noMerchant
                ? "Select a merchant"
                : quick === "soon"
                  ? "Nothing comes back within the hour."
                  : quick === "long"
                    ? "Nothing is snoozed for a week or more."
                    : debouncedSearch || branchFilter !== "all"
                      ? "Nothing snoozed matches this filter."
                      : "Nothing is snoozed right now."}
            </p>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                {view !== "rows" ? renderGrouped() : (
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      {!readOnly && (
                        <th style={{ ...tbl.th, width: 40 }}>
                          <input
                            type="checkbox"
                            checked={allPagedSelected}
                            onChange={toggleAllRows}
                            style={{ width: 16, height: 16, accentColor: C.coral }}
                          />
                        </th>
                      )}
                      {["Product", "Branch", "Back on the menu", "Left", "By", ...(readOnly ? [] : [""])].map((label, i) => (
                        <th key={`${label}-${i}`} style={{ ...tbl.th, ...(label === "" && { textAlign: "right" }) }}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((row) => {
                      const key = rowKey(row);
                      return (
                        <tr key={row.id ?? key} className="rpt-row">
                          {!readOnly && (
                            <td style={tbl.td}>
                              <input
                                type="checkbox"
                                checked={selectedKeys.has(key)}
                                onChange={() => toggleRow(row)}
                                style={{ width: 16, height: 16, accentColor: C.coral }}
                              />
                            </td>
                          )}
                          <td style={tbl.td}>{productCell(row.Product, row.merchant_product_id)}</td>
                          <td style={tbl.td}>{branchLabel(rowBranchId(row), row.Branch?.name || "—")}</td>
                          <td style={{ ...tbl.td, fontVariantNumeric: "tabular-nums" }}>{formatDate(row.snooze_until)}</td>
                          <td style={tbl.td}>{leftBadge(row.snooze_until)}</td>
                          <td style={tbl.td}>{byCell(row)}</td>
                          {!readOnly && (
                            <td style={{ ...tbl.td, textAlign: "right" }}>
                              <Button
                                size="sm"
                                style={{ ...ghostButton, color: C.coral, fontSize: 11, padding: "5px 12px" }}
                                onClick={() => unsnooze([toRow(row)])}
                              >
                                Un-snooze
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                )}
              </div>
              <TablePagination
                total={total}
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </>
          )}
        </RCard>
      </Container>

      {/* ── Snooze modal */}
      <Modal isOpen={snoozeOpen} toggle={() => setSnoozeOpen(false)} size="lg">
        <ModalHeader toggle={() => setSnoozeOpen(false)}>Snooze products</ModalHeader>
        <ModalBody>
          <Label style={{ fontWeight: 600 }}>Branches *</Label>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <MultiSelectField
                options={branchOptions}
                value={modalBranches}
                onChange={(next) => setModalBranches(next || [])}
                placeholder="Choose one or more branches"
              />
            </div>
            <Button
              size="sm"
              style={ghostButton}
              onClick={() => setModalBranches(modalBranches.length === branchOptions.length ? [] : branchOptions)}
            >
              {modalBranches.length === branchOptions.length ? "None" : "All"}
            </Button>
          </div>

          <Label style={{ fontWeight: 600, marginTop: 12 }}>For how long *</Label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {DURATIONS.map((d) => (
              <Button
                key={d.key}
                size="sm"
                onClick={() => setDuration(d.key)}
                style={duration === d.key ? primaryButton : ghostButton}
              >
                {d.label}
              </Button>
            ))}
          </div>
          {DURATIONS.find((d) => d.key === duration)?.custom && (
            <Input
              type="datetime-local"
              value={customUntil}
              onChange={(e) => setCustomUntil(e.target.value)}
              style={{ ...fieldStyle, maxWidth: 260, marginBottom: 4 }}
            />
          )}
          {duration === "eod" && (
            <div style={{ fontSize: 12, color: C.textLight }}>
              Midnight in the merchant’s own timezone, worked out on the server — not this browser’s.
            </div>
          )}

          <Label style={{ fontWeight: 600, marginTop: 16 }}>
            Products * {picked.size > 0 && <span style={{ color: C.coral }}>({picked.size} selected)</span>}
          </Label>
          <Input
            type="text"
            placeholder="Search products…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            style={{ ...fieldStyle, marginBottom: 8 }}
          />

          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, maxHeight: 300, overflowY: "auto" }}>
            {productsLoading ? (
              <Loader />
            ) : products.length === 0 ? (
              <p style={{ textAlign: "center", padding: 24, color: C.textLight, margin: 0 }}>
                No products match.
              </p>
            ) : (
              <>
                <label
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                    borderBottom: `1px solid ${C.borderLight}`, background: C.bg, margin: 0, cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allOnPagePicked}
                    onChange={toggleAllOnPage}
                    style={{ width: 16, height: 16, accentColor: C.coral }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 13, color: C.textMed }}>Select all on this page</span>
                </label>
                {products.map((p) => {
                  const alreadySnoozed = (p.BranchSnoozedProducts || []).length > 0;
                  return (
                    <label
                      key={p.id}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                        borderBottom: `1px solid ${C.borderLight}`, margin: 0, cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={picked.has(p.id)}
                        onChange={() => togglePicked(p)}
                        style={{ width: 16, height: 16, accentColor: C.coral }}
                      />
                      <span style={{ flex: 1, color: C.text }}>{p.title}</span>
                      {!p.merchant_product_id && (
                        <Badge color={C.textMed} bg={C.borderLight}>No POS id</Badge>
                      )}
                      {alreadySnoozed && <Badge color={C.orange} bg={C.orangeLight}>Snoozed</Badge>}
                    </label>
                  );
                })}
              </>
            )}
          </div>

          {productTotal > PRODUCT_PAGE_SIZE && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              <Button size="sm" style={ghostButton} disabled={productPage <= 1} onClick={() => setProductPage((p) => p - 1)}>
                ← Prev
              </Button>
              <span style={{ fontSize: 12, color: C.textMed }}>
                Page {productPage} of {Math.ceil(productTotal / PRODUCT_PAGE_SIZE)}
              </span>
              <Button
                size="sm"
                style={ghostButton}
                disabled={productPage >= Math.ceil(productTotal / PRODUCT_PAGE_SIZE)}
                onClick={() => setProductPage((p) => p + 1)}
              >
                Next →
              </Button>
              <span style={{ fontSize: 12, color: C.textLight }}>
                Selections are kept as you page and search.
              </span>
            </div>
          )}

          {picked.size > 0 && (
            <div style={{ fontSize: 12, color: C.textMed, marginTop: 10 }}>
              {[...picked].slice(0, 6).map((id) => pickedLabels.get(id) || `#${id}`).join(", ")}
              {picked.size > 6 && ` and ${picked.size - 6} more`}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button style={{ ...ghostButton, padding: "8px 20px" }} onClick={() => setSnoozeOpen(false)}>Cancel</Button>
          <Button style={{ ...primaryButton, padding: "8px 20px" }} disabled={saving} onClick={handleSnooze}>
            {saving ? "Snoozing…" : `Snooze${modalBranches.length > 1 ? ` at ${modalBranches.length} branches` : ""}`}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Report modal. Shown whenever anything was skipped: each reason is fixed elsewhere, and
             only the operator can do it, so it has to reach them as words rather than a count. */}
      <Modal isOpen={!!report} toggle={() => setReport(null)} size="lg">
        <ModalHeader toggle={() => setReport(null)}>
          {report?.kind === "unsnooze" ? "Un-snooze result" : "Snooze result"}
        </ModalHeader>
        <ModalBody>
          {[
            { key: "applied", label: "Snoozed", color: C.green, bg: C.greenLight },
            { key: "extended", label: "Extended (were already snoozed)", color: C.blue, bg: C.blueLight },
            { key: "lifted", label: "Un-snoozed", color: C.green, bg: C.greenLight },
          ].map(({ key, label, color, bg }) => {
            const list = report?.[key] || [];
            if (list.length === 0) return null;
            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Badge color={color} bg={bg}>{`${list.length} ${label}`}</Badge>
                </div>
                <div style={{ fontSize: 13, color: C.textMed }}>
                  {list.map((item, i) => (
                    <div key={`${item.branchId}-${item.productId}-${i}`}>
                      {item.product} — {item.branch}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {(report?.skipped || []).length > 0 && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <Badge color={C.orange} bg={C.orangeLight}>{`${report.skipped.length} skipped`}</Badge>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      {["Product", "Branch", "Why"].map((label) => (
                        <th key={label} style={tbl.th}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.skipped.map((s, i) => (
                      <tr key={`${s.branchId}-${s.productId}-${i}`}>
                        <td style={tbl.td}>{s.product}</td>
                        <td style={tbl.td}>{s.branch || <span style={{ color: C.textLight }}>all</span>}</td>
                        <td style={{ ...tbl.td, color: C.textMed }}>{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button style={{ ...ghostButton, padding: "8px 20px" }} onClick={() => setReport(null)}>Close</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default SnoozedProducts;
